import { useEffect, useState } from 'react'
import { contractsApi, type PortalIssue } from '../api/contracts'
import { Button, Callout, TextField } from './ui'

const quando = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''

/**
 * Problema segnalato dal portale per una pratica sospesa (o rifiutata/annullata): stato e motivo del portale, e per le
 * sospese il passaggio "Risolto, al reparto": chi ha corretto scrive cosa ha sistemato, il backoffice riceve una
 * notifica da gestire e la pratica torna in lavorazione quando il portale cambia stato (allineamento automatico).
 */
export function PortalIssuePanel({ contractId, status, onChanged }: { contractId: number; status: string; onChanged: () => void }) {
  const [issue, setIssue] = useState<PortalIssue | null>(null)
  const [nota, setNota] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    // In lavorazione: si mostra solo la risoluzione fatta nel CRM (il server risponde null se non c'è)
    if (!['Sospesa', 'Rifiutata', 'Annullata', 'InLavorazioneOperatore'].includes(status)) { setIssue(null); return }
    contractsApi.getPortalIssue(contractId)
      .then(p => { if (alive) setIssue(p) })
      .catch(() => { if (alive) setIssue(null) })
    return () => { alive = false }
  }, [contractId, status])

  if (!issue || (!issue.motivo && !issue.statoPortale && !issue.risolta)) return null

  async function risolvi() {
    setBusy(true)
    setError(null)
    try {
      const r = await contractsApi.resolveSuspension(contractId, nota)
      setIssue(r.problema)
      setNota('')
      const avvisi = r.notificati > 0 ? ` Avvisate ${r.notificati} persone (amministratori e backoffice).` : ''
      setMsg(r.rinviata
        ? `Pratica rimessa in lavorazione e correzione rinviata su ${r.problema?.portale ?? 'Teamwork'}.${avvisi}`
        : `Pratica rimessa in lavorazione. Su ${r.problema?.portale ?? 'questo portale'} la correzione va riportata a mano dal backoffice.${avvisi}`)
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operazione non riuscita.')
    } finally {
      setBusy(false)
    }
  }

  const sospesa = status === 'Sospesa'
  return (
    <Callout tone={issue.risolta ? 'info' : 'warning'}>
      <strong>{issue.risolta && status === 'InLavorazioneOperatore' ? 'Sospensione risolta, in lavorazione' : `${sospesa ? 'Sospesa' : status} su ${issue.portale}`}{issue.statoPortale && !(issue.risolta && status === 'InLavorazioneOperatore') ? `: ${issue.statoPortale}` : ''}</strong>
      {issue.segnalatoIl && <span className="md-typescale-body-small"> · segnalato il {quando(issue.segnalatoIl)}</span>}
      {issue.motivo && <div style={{ marginTop: 4 }}>Motivo: <strong>{issue.motivo}</strong></div>}
      {issue.risolta && !sospesa && (
        <div style={{ marginTop: 6 }}>
          ✓ Risolta il {quando(issue.risoltaIl)}: {issue.risoltaNota} — la pratica è ripartita; {issue.portale} la rilavora e lo stato si aggiorna da solo (il motivo era: {issue.motivo ?? issue.statoPortale ?? '—'}).
        </div>
      )}
      {sospesa && issue.risoltaIl && (
        <div className="md-typescale-body-small" style={{ marginTop: 6 }}>
          Segnata risolta il {quando(issue.risoltaIl)} ({issue.risoltaNota}), ma la pratica è ancora sospesa: premi di nuovo il pulsante per rimetterla in lavorazione.
        </div>
      )}
      {sospesa && (
        <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
          <span className="md-typescale-body-small">
            Correggi i dati nella pratica (Dati per il portale, allegati) e poi scrivi cosa hai sistemato:
            {issue.portale === 'Teamwork'
              ? ' la correzione viene rinviata su Teamwork con la nota per il backoffice, e la pratica riparte da lì.'
              : ` su ${issue.portale} la correzione va riportata a mano: avvisiamo amministratori e backoffice con la tua nota.`}
          </span>
          <TextField id="risoltaNota" label="Cosa hai corretto" value={nota} maxLength={1000} disabled={busy}
            onChange={e => setNota(e.target.value)} />
          <div>
            <Button type="button" variant="filled" disabled={busy || nota.trim().length === 0} onClick={() => { void risolvi() }}>
              {busy ? 'Invio…' : issue.portale === 'Teamwork' ? 'Risolto: rinvia su Teamwork' : 'Risolto: avvisa il reparto'}
            </Button>
          </div>
        </div>
      )}
      {msg && <div className="md-typescale-body-small" style={{ marginTop: 6 }}>{msg}</div>}
      {error && <div className="md-typescale-body-small" style={{ marginTop: 6, color: 'var(--md-sys-color-error)' }}>{error}</div>}
    </Callout>
  )
}
